import { FC, useState, MouseEvent, useEffect, useMemo } from 'react';
import { useQuery, useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { Dialog, Grid, LinearProgress, Popover } from '@material-ui/core';
import _MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import SettingsIcon from '@material-ui/icons/Settings';
import _HelpIcon from '@material-ui/icons/Help';
import CancelIcon from '@material-ui/icons/Cancel';
import styled from '@emotion/styled';
import QRCode from 'qrcode.react';

import { lastAccessedOrganizationVar } from '../../../../cache';
import { COLOR_TEXT_MAIN } from '../../../../theme/colors';
import Spinner from '../../primitive/Spinner';
import { ModalContent, ModalFooter, ModalText, ModalTitle } from '../../primitive/Modal';
import { PrimaryButton, PrimarySmallButton, SecondaryButton, SecondarySmallButton } from './common';
import { usePreview, GetPreview } from '../../../../hooks/usePreview';
import { getURLWithQueryAdd } from '../../../../utility/getURLWithQueryAdd';
import {
  GetOrganizationDocument,
  GetOrganizationQuery,
  GetOrganizationQueryVariables,
  GetVariationVersionsDocument,
  GetVariationVersionsQuery,
  GetVariationVersionsQueryVariables,
  Preview,
  PossibleProject,
  Variation,
  Version,
} from '../../../../generated/graphql';
import { convertJST } from '../../../../utility/convertJST';

const PreviewActions: FC<{ project: PossibleProject; variation: Variation; refetch: () => void }> = ({
  project,
  variation,
  refetch,
}) => {
  const organization = useReactiveVar(lastAccessedOrganizationVar);
  const { data: { getOrganization: k2organization } = {} } = useQuery<
    GetOrganizationQuery,
    GetOrganizationQueryVariables
  >(GetOrganizationDocument, {
    variables: { id: organization!.id },
    context: { clientName: 'planbcd' },
    skip: !organization,
  });

  const [getVersions] = useLazyQuery<GetVariationVersionsQuery, GetVariationVersionsQueryVariables>(
    GetVariationVersionsDocument,
    {
      variables: { variation_id: variation.id },
      context: { clientName: 'planbcd' },
    },
  );

  const { preview, getPreview } = usePreview({ variation, projectType: project.project_type });
  const [applicablePreview, setApplicablePreview] = useState<Preview | null>();
  const [interactionStatus, setInteractionStatus] = useState({
    changeScreenshot: false,
    previewWithStorage: false,
    previewWithProxy: false,
  });
  const [hoverAnchor, setHoverAnchor] = useState<SVGSVGElement | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<SVGSVGElement | null>(null);

  const handleHoveroverOpen = (event: MouseEvent<SVGSVGElement>) => {
    setHoverAnchor(event.currentTarget);
  };
  const handleHoveroverClose = () => {
    setTimeout(() => setHoverAnchor(null), 3000);
  };

  const handlePopoverOpen = (event: MouseEvent<SVGSVGElement>) => {
    setPopoverAnchor(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setPopoverAnchor(null);
  };
  const toggleChangeScreenshotModal = () => {
    setInteractionStatus((prev) => ({ ...prev, changeScreenshot: !prev.changeScreenshot }));
  };
  const togglePreviewModalWithStorage = () => {
    setInteractionStatus((prev) => ({ ...prev, previewWithStorage: !prev.previewWithStorage }));
  };
  const togglePreviewModalWithProxyOpen = () => {
    setInteractionStatus((prev) => ({ ...prev, previewWithProxy: !prev.previewWithProxy }));
  };

  const hoverOpen = Boolean(hoverAnchor);
  const popoverOpen = Boolean(popoverAnchor);

  const onClickCheckPreview = () => {
    const _url = new URL(project.url);
    console.log('preview', preview);
    if (preview?.query_string) {
      window.open(`${getURLWithQueryAdd(project.url, preview.query_string)}`, '', '');
    } else if (variation?.plan_a === true) {
      _url.searchParams.set('_kz_void', '1');
      window.open(`${_url}`, '', '');
    } else if (project?.project_type === 'redirect_test') {
      window.open(`${variation?.redirect_url}`, '', '');
    }
    window.open(`${getURLWithQueryAdd(project.url)}`);
  };

  const UrlForPreviewWithStorage = useMemo(() => {
    if (!preview) return '';
    const _url = new URL(project.project_verify_urls[0]?.verify_url || variation.project.url);
    const _params = new URLSearchParams(preview.url.replace(/^.*\?/, ''));
    _url.searchParams.set('_kz_debug', '2');
    _url.searchParams.set(
      '_kz_store_preview',
      `${project.id}.${variation.id}.${_params.get('_kz_preview_hash')}.${_params.get(
        '_kz_preview_token',
      )}.${_params.get('_kz_preview_timestamp')}`,
    );
    return _url.toString();
  }, [preview, project, variation]);

  const hasDifferentVersions = useMemo(() => {
    return !!variation.applicable_version_id;
  }, [variation]);

  const canExpandMore = useMemo(() => {
    if (variation?.plan_a) {
      return false;
    }
    if (variation?.applicable_version_id) {
      return false;
    }
    if (project?.project_type === 'redirect_test') {
      return false;
    }
    return true;
  }, [variation, project]);

  useEffect(() => {
    if (hasDifferentVersions) {
      getVersions().then(({ data: versionsData }) => {
        const { versions } = versionsData!;
        const versionCurrentlyUsing = versions.find(
          (v: Version) => v.id === variation.applicable_version_id,
        )?.version_num;
        getPreview({ live: true, useProxy: false, version: versionCurrentlyUsing }).then((data) => {
          if (!data) setApplicablePreview(null);
          setApplicablePreview(data!.generatePreview);
        });
      });
    }
  }, [hasDifferentVersions]);

  return (
    <>
      <SecondarySmallButton
        disabled={variation?.permissions?.update_screenshot_image?.permit ? false : true}
        onClick={toggleChangeScreenshotModal}
      >
        <SettingsIcon />
        スクリーンショットを変更する
      </SecondarySmallButton>
      <ChangeScreenshotModal
        variationId={variation.id}
        isOpen={interactionStatus.changeScreenshot}
        onClose={toggleChangeScreenshotModal}
        refetch={refetch}
      />
      <HelpIcon onMouseEnter={handleHoveroverOpen} onMouseLeave={handleHoveroverClose} />
      <Popover
        id="mouse-over-popover"
        open={hoverOpen}
        anchorEl={hoverAnchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleHoveroverClose}
        disableRestoreFocus
      >
        <Pop>
          {variation?.plan_a
            ? '「プレビューを見る」の有効期限について、組織内ユーザーは1週間、それ以外は2分です。'
            : '「プレビューを見る」の有効期限は、組織内ユーザーは1週間、それ以外は2分です。「直接開けないページ」はフォームなど直接アクセスできないページの確認に、「プロキシ付きURL」は対象ページにJSタグ未設置の場合にご利用ください。'}
        </Pop>
      </Popover>
      {hasDifferentVersions ? (
        <>
          <PrimarySmallButton
            disabled={!preview}
            onClick={() => {
              window.open(`${getURLWithQueryAdd(project.url, preview!.query_string)}`);
            }}
          >
            使用中デザインのプレビューを見る
          </PrimarySmallButton>
          <PrimarySmallButton
            disabled={!applicablePreview}
            onClick={() => {
              window.open(`${getURLWithQueryAdd(project.url, applicablePreview?.query_string ?? '')}`);
            }}
          >
            反映待ちデザインのプレビューを見る
          </PrimarySmallButton>
        </>
      ) : (
        <PrimarySmallButton disabled={!variation?.permissions?.show?.permit} onClick={onClickCheckPreview}>
          プレビューで見る
        </PrimarySmallButton>
      )}

      {canExpandMore && (
        <>
          <MoreHorizIcon onClick={handlePopoverOpen} />
          <Popover
            id="mouse-over-popover"
            open={popoverOpen}
            anchorEl={popoverAnchor}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            <PopMenu>
              {k2organization && k2organization.sitejs_version === 3 && (
                <>
                  <div onClick={togglePreviewModalWithStorage}>
                    <span>直接開けないページURLで共有する</span>
                  </div>
                  <PreviewModalWithStorage
                    previewUrl={UrlForPreviewWithStorage}
                    isOpen={interactionStatus.previewWithStorage}
                    onClose={togglePreviewModalWithStorage}
                  />
                  <hr />
                </>
              )}
              <div onClick={togglePreviewModalWithProxyOpen}>
                <span>プロキシ付きプレビューURLで共有する</span>
              </div>
              <PreviewModalWithProxy
                getPreview={getPreview}
                isOpen={interactionStatus.previewWithProxy}
                onClose={togglePreviewModalWithProxyOpen}
              />
            </PopMenu>
          </Popover>
        </>
      )}
    </>
  );
};

import useFileUpload from '../../../../hooks/useFileUpload';
import {
  ChangeScreenshotDocument,
  ChangeScreenshotMutation,
  ChangeScreenshotMutationVariables,
} from '../../../../generated/graphql';
import mutationVariables from '../../../../utility/mutationVariables';

export const ChangeScreenshotModal: FC<{
  variationId: string;
  refetch: () => void;
  isOpen: boolean;
  onClose: () => void;
}> = ({ variationId, refetch, isOpen, onClose }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { files, onDrop, removeFile } = useFileUpload();
  const [changeScreenshot] = useMutation<ChangeScreenshotMutation, ChangeScreenshotMutationVariables>(
    ChangeScreenshotDocument,
    {
      context: { clientName: 'planbcd' },
    },
  );

  const onClickSave = () => {
    setIsProcessing(true);
    changeScreenshot(
      mutationVariables({
        id: variationId,
        data: files[0],
      }),
    )
      .then(() => {
        refetch();
      })
      .catch(() => {
        files.map((_, i) => {
          removeFile(i, 'up-screenshot');
        });
      })
      .finally(() => {
        onClose();
        setIsProcessing(false);
      });
  };

  const SelectedImages = useMemo(
    () =>
      files.map((file, i) => (
        <Grid key={`selecetd-image-${i}`} container spacing={2} alignItems="flex-start" justifyContent="space-around">
          <CancelIcon style={{ color: '#BF4140', cursor: 'pointer' }} onClick={() => removeFile(i, 'up-screenshot')} />
          <UploadedScreenshot src={window.URL.createObjectURL(file)} />
          <span>{file.name}</span>
        </Grid>
      )),
    [files],
  );

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalTitle>スクリーンショットを変更する</ModalTitle>
      </ModalContent>
      <ModalContent>
        <GappedGrid container direction="column" alignItems="flex-start">
          {/* @ts-ignore: ts(2322) component prop does accept html tag names */}
          <SecondaryButton component="label">
            スクリーンショット画像を選択する
            <input id="up-screenshot" hidden type="file" accept="image/*,.png,.jpg,.jpeg,.gif" onChange={onDrop} />
          </SecondaryButton>
          {SelectedImages}
        </GappedGrid>
      </ModalContent>
      {isProcessing && (
        <Grid item>
          <LinearProgress />
        </Grid>
      )}
      <ModalFooter>
        <SecondaryButton onClick={onClose}>キャンセル</SecondaryButton>
        <PrimaryButton onClick={onClickSave} disabled={isProcessing}>
          保存する
        </PrimaryButton>
      </ModalFooter>
    </Dialog>
  );
};

export const PreviewModalWithStorage: FC<{
  previewUrl: string;
  isOpen: boolean;
  onClose: () => void;
}> = ({ previewUrl, isOpen, onClose }) => {
  const resetPreviewWithStorage = previewUrl
    .replace(/_kz_store_preview=.*(&|)?/, '_kz_store_preview=0')
    .replace(/_kz_debug=\d/, '_kz_debug=0');

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalTitle>直接開けないページの確認</ModalTitle>
      </ModalContent>
      <ModalContent>
        <ModalText>フォームなど直接アクセスできないページでデザインのプレビュー確認ができます。</ModalText>
        <ModalText>
          下記URLから、対象ページへアクセスしてください。このURLはデザイン案が端末にセットされてから2時間後まで有効です。本機能についての詳しい説明は
          <a
            href="https://support.kaizenplatform.net/hc/ja/articles/4409337793433"
            style={{ color: '#006BD6', textDecoration: 'none' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            サポートページ
          </a>
          をご覧ください。
        </ModalText>
        <div style={{ margin: '20px auto', width: '128px' }}>
          <QRCode value={previewUrl} />
        </div>
        <Grid item xs={12}>
          <ScriptContainer>
            <div style={{ marginRight: '20px', width: '100%', overflow: 'scroll' }}>{previewUrl}</div>
          </ScriptContainer>
        </Grid>
      </ModalContent>
      <ModalFooter>
        <SecondaryButton onClick={onClose}>キャンセル</SecondaryButton>
        <SecondaryButton
          onClick={() => {
            window.open(resetPreviewWithStorage, '_blank');
          }}
        >
          デザイン案のセットを解除
        </SecondaryButton>
        <PrimaryButton onClick={() => navigator.clipboard.writeText(previewUrl)}>URLをコピー</PrimaryButton>
      </ModalFooter>
    </Dialog>
  );
};

export const PreviewModalWithProxy: FC<{
  getPreview: GetPreview;
  isOpen: boolean;
  onClose: () => void;
}> = ({ getPreview, isOpen, onClose }) => {
  const [preview, setPreview] = useState<Preview | null>();
  useEffect(() => {
    getPreview({ live: false, useProxy: true }).then((data) => {
      if (!data) setPreview(null);
      setPreview(data!.generatePreview);
    });
  }, []);

  if (!preview)
    return (
      <Dialog open={isOpen} onClose={onClose}>
        <ModalContent>
          <Spinner />
        </ModalContent>
      </Dialog>
    );

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalTitle>プロキシ付きプレビューURL</ModalTitle>
      </ModalContent>
      <ModalContent>
        <ModalText>
          {`このURLは${
            preview?.expires_at ? convertJST(preview?.expires_at) : '-'
          }まで有効です。有効期限が切れた場合、再度この画面を表示すると新しいURLを取得できます。`}
        </ModalText>
        <div style={{ margin: '20px auto', width: '128px' }}>
          <QRCode value={preview.url} />
        </div>
        <Grid item xs={12}>
          <ScriptContainer>
            <div style={{ marginRight: '20px', width: '100%', overflow: 'scroll' }}>{preview.url}</div>
          </ScriptContainer>
        </Grid>
      </ModalContent>
      <ModalFooter>
        <SecondaryButton onClick={onClose}>閉じる</SecondaryButton>
        <PrimaryButton
          onClick={() => {
            navigator.clipboard.writeText(preview?.url ?? '').then(
              function () {
                console.log('copied');
              },
              function (err) {
                console.error(err);
              },
            );
          }}
        >
          URLをコピー
        </PrimaryButton>
      </ModalFooter>
    </Dialog>
  );
};

const GappedGrid = styled(Grid)(`
  gap: 2rem;
`);

const UploadedScreenshot = styled('img')(`
  max-width: 30%;
`);

const HelpIcon = styled(_HelpIcon)(`
  color: ${COLOR_TEXT_MAIN};
`);

const Pop = styled('div')(`
  line-height: 22px;
  color: #fff;
  text-align: left;
  padding: 24px;
  font-size: 14px;
  background-color: #4C566CEE; 
  box-shadow: 0px 1px 9px rgba(0, 0, 0, 0.04);
`);

const MoreHorizIcon = styled(_MoreHorizIcon)(`
  cursor: pointer;
  color: ${COLOR_TEXT_MAIN};
`);

const PopMenu = styled('div')(`
  line-height: 22px;
  color: #4C566C;
  text-align: left;
  border-radius: 8px;
  width: 290px;
  padding: 0;
  font-size: 14px;
  background-color: #fff; 
  box-shadow: 0px 1px 9px rgba(0, 0, 0, 0.04);
  bottom: 50px;
  right: 0px;
  div {
    font-weight: 700;
    height: 40px;
    position: relative;
    :hover {
      background-color: #F9F9F9;
      color: #707889;
    }
    span {
      position: absolute;
      top: calc(50% - 11px);
      left: 16px;
    }
  }
  hr {
    margin: 0 16px;
    border: none;
    background-color: #E6E7E8;
    height: 1px;
  }
`);

const ScriptContainer = styled('div')(`
  background: #4C566C;
  border-radius: 8px;
  color: white;
  padding: 24px;
  user-select: all;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  white-space: nowrap;
`);

export default PreviewActions;
